/**
 * Cart Recommendations ("Pairs Well With")
 * ----------------------------------------
 * Custom element wrapper for the cart-drawer recommendation row.
 *
 * WHY A CUSTOM ELEMENT (do not revert to an inline <script>):
 * The cart drawer lives in the header section, which this theme updates via
 * DOM morphing rather than full page loads. A <script> injected through a morph
 * (innerHTML) never executes, so an inline module only ran on hard page loads and
 * silently no-op'd on client-side navigation — leaving the skeletons up forever.
 * A custom element's connectedCallback fires every time the element enters the DOM,
 * however it got there, so binding here is morph-safe.
 *
 * Logic (fetch / parse / cache / cart-dedupe) is unchanged from the previous inline
 * version — only the trigger for binding moved from "inline script on parse" to
 * "connectedCallback on connect".
 */
class CartRecommendationsComponent extends HTMLElement {
  connectedCallback() {
    // Bind document-level listeners exactly once, no matter how many times an
    // instance mounts/re-mounts across morphs. Listeners query the current instance
    // fresh (never capture `this`) so they survive the element being replaced.
    if (!window.__cartRecsBound) {
      window.__cartRecsBound = true;

      // 'dialog:open' doesn't bubble — listen in the capture phase, scoped to the drawer.
      document.addEventListener(
        'dialog:open',
        (event) => {
          const root = document.querySelector('cart-recommendations-component');
          if (root && event.target instanceof Element && event.target.contains(root)) {
            root.load();
          }
        },
        true
      );

      // On any cart change: reseed off the newest line, bust the cache (so the
      // server-side dedupe re-runs against the updated cart and the just-added item
      // drops out), and reload.
      document.addEventListener('cart:update', (event) => {
        const root = document.querySelector('cart-recommendations-component');
        if (!root) return;

        const items = event.detail?.resource?.items;
        const lastItem = items && items.length ? items[items.length - 1] : null;
        if (!lastItem) {
          root.hidden = true;
          return;
        }
        root.hidden = false;
        root.dataset.productId = String(lastItem.product_id);

        const cache = (window.__cartRecsCache ||= {});
        for (const key in cache) delete cache[key];
        delete root.dataset.loadedFor;
        root.load();
      });
    }

    // If the drawer is already open at the moment this element connects
    // (e.g. re-rendered while open), populate immediately instead of waiting
    // for the next open/update event.
    if (this.closest('dialog[open]')) this.load();
  }

  load() {
    const cache = (window.__cartRecsCache ||= {});
    const { url, productId, sectionId, intent } = this.dataset;
    if (!url || !productId || !sectionId) return;

    const content = this.querySelector('.cart-recommendations__content');
    if (!content || this.dataset.loadedFor === productId) return;

    if (cache[productId] != null) {
      content.innerHTML = cache[productId];
      this.dataset.loadedFor = productId;
      return;
    }

    fetch(`${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#cart-recommendations .cart-recommendations__content');
        if (!fresh) return;
        cache[productId] = fresh.innerHTML;
        content.innerHTML = fresh.innerHTML;
        this.dataset.loadedFor = productId;
      })
      .catch(() => {});
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
